<template>
  <div class="countdown-timer w-full">
    <div
      class="mb-35 flex items-start justify-center gap-10 sm:mb-45 sm:gap-20 lg:mb-55 lg:gap-25"
    >
      <div
        v-for="(unit, idx) in units"
        :key="unit.key"
        class="flex items-start"
      >
        <div class="flex flex-col items-center">
          <div
            class="flex h-[62px] w-[62px] items-center justify-center rounded-[10px] max-sm:rounded-[5px] bg-lightBlue sm:h-[110px] sm:w-[110px] lg:h-[157px] lg:w-[157px] border-[2px] border-blue"
          >
            <span
              class="font-degular text-[100px] font-semibold leading-[60px] text-blue max-sm:text-[40px] pb-5"
            >
              {{ unit.value }}
            </span>
          </div>
          <span
            class="mt-10 font-degular text-[12px] font-bold uppercase tracking-wide text-blue max-sm:mt-5 sm:text-sm lg:text-[25px]"
          >
            {{ unit.label }}
          </span>
        </div>

        <span
          v-if="idx < units.length - 1"
          aria-hidden="true"
          class="ml-10 flex h-[157px] max-sm:h-[62px] items-center font-degular text-[28px] font-semibold leading-none text-blue sm:ml-20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="49"
            viewBox="0 0 14 49"
            fill="none"
            class="max-sm:h-20"
          >
            <path
              d="M6.60058 13.1726C10.246 13.1726 13.2011 10.2238 13.2011 6.5863C13.2011 2.94879 10.246 0 6.60058 0C2.95519 0 0 2.94879 0 6.5863C0 10.2238 2.95519 13.1726 6.60058 13.1726Z"
              fill="#0057C2"
            />
            <path
              d="M6.60058 48.5901C10.246 48.5901 13.2011 45.6413 13.2011 42.0038C13.2011 38.3663 10.246 35.4175 6.60058 35.4175C2.95519 35.4175 0 38.3663 0 42.0038C0 45.6413 2.95519 48.5901 6.60058 48.5901Z"
              fill="#0057C2"
            />
          </svg>
        </span>
      </div>
    </div>

    <form
      class="mx-auto flex w-full items-center rounded-[30px] border-[2px] border-blue bg-white  pl-35 max-sm:pl-25"
      @submit.prevent="handleSubmit"
    >
      <input
        v-model="email"
        type="email"
        :placeholder="placeholder"
        class="h-50 max-sm:h-40 min-w-0 flex-1 border-0 bg-transparent text-sm text-gray placeholder-gray outline-none focus:ring-0 sm:h-45 sm:text-base font-avantgarde font-medium"
        required
      />
      <button
        type="submit"
        :disabled="isLoading || !listId"
        class="h-40 shrink-0 rounded-[30px] bg-blue px-20 font-avantgarde font-medium capitalize text-white transition-colors hover:bg-green hover:text-blue disabled:opacity-60 sm:h-50 sm:px-50"
      >
        {{ buttonText }}
      </button>
    </form>

    <div
      v-if="statusText"
      :class="[
        'mt-15 text-center text-sm',
        isError ? 'text-error' : 'text-blue',
      ]"
      role="status"
    >
      {{ statusText }}
    </div>
  </div>
</template>

<script>
export default {
  name: "CountdownTimer",
};
</script>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {subscribe} from "klaviyo-subscribe";

const props = defineProps({
  deadline: {
    type: String,
    required: true,
  },
  listId: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Email Address",
  },
  buttonText: {
    type: String,
    default: "Notify Me",
  },
  successMessage: {
    type: String,
    default: "Thanks — we'll be in touch!",
  },
  labels: {
    type: Object,
    default: () => ({
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds",
    }),
  },
});

const pad = n => String(Math.max(0, n)).padStart(2, "0");

const parseDeadline = value => {
  if (!value) return null;
  const hasTZ = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTZ ? value : value.replace(" ", "T");
  const ts = new Date(normalized).getTime();
  return Number.isNaN(ts) ? null : ts;
};

const targetTs = parseDeadline(props.deadline) ?? Date.now();
const now = ref(Date.now());

let intervalId = null;
onMounted(() => {
  now.value = Date.now();
  intervalId = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
  console.log(props.listId)
});
onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});

const units = computed(() => {
  const diff = Math.max(0, targetTs - now.value);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return [
    {key: "days", label: props.labels.days, value: pad(days)},
    {key: "hours", label: props.labels.hours, value: pad(hours)},
    {key: "minutes", label: props.labels.minutes, value: pad(minutes)},
    {key: "seconds", label: props.labels.seconds, value: pad(seconds)},
  ];
});

const email = ref("");
const isLoading = ref(false);
const statusText = ref("");
const isError = ref(false);
let statusTimeout = null;

const setStatus = (text, error = false) => {
  statusText.value = text;
  isError.value = error;
  if (statusTimeout) clearTimeout(statusTimeout);
  if (text) {
    statusTimeout = window.setTimeout(() => {
      statusText.value = "";
    }, 5000);
  }
};

onBeforeUnmount(() => {
  if (statusTimeout) clearTimeout(statusTimeout);
});

const handleSubmit = async () => {
  if (!props.listId || !email.value || isLoading.value) return;

  isLoading.value = true;
  statusText.value = "";
  isError.value = false;

  try {
    const resp = await subscribe(props.listId, email.value);
    if (!resp?.success) {
      const msg = (resp?.errors || []).join(" ") || "Something went wrong.";
      setStatus(msg, true);
    } else {
      email.value = "";
      setStatus(props.successMessage, false);
    }
  } catch (e) {
    setStatus(e?.message || "Something went wrong.", true);
  } finally {
    isLoading.value = false;
  }
};
</script>
